import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { computePreset } from '../../AnalyticsPro/DateRangeControl';
import { useProperties } from '../filters/useProperties';
import { formatNumber } from '../../Shared/Formatters';

/**
 * Traffic widget — footfall trend with its OWN property and date selection.
 *
 * Every other widget follows the dashboard filter bar. This one doesn't: people
 * read traffic against a different window than sales (yesterday's hour-by-hour
 * profile next to a year-to-date revenue tile), and footfall coverage often
 * starts later than the sales feed, so a shared date range shows an empty chart
 * next to healthy numbers.
 *
 * The selection lives in the widget's per-instance `config`, so it is saved with
 * the layout and two copies of the widget can watch two different properties.
 * The backend re-validates the chosen property against the caller's scope —
 * config is client input like any other (see widgetController.getData).
 */

const DEFAULT_RANGE_KEY = 'last30';

// Two controls on one line is the budget for a 6x4 card. The seven preset
// buttons from the analytics page wrap to three rows here and swallow the chart.
const PRESETS = [
    // No "Today": footfall arrives on a delay, so today's bar is always a
    // partial count that reads as a collapse in traffic. Yesterday is the
    // earliest window that means anything. Custom… can still reach it.
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7', label: 'Last 7 days' },
    { key: 'last30', label: 'Last 30 days' },
    { key: 'mtd', label: 'Month to date' },
    { key: 'qtd', label: 'Quarter to date' },
    { key: 'ytd', label: 'Year to date' },
];

function detectPreset(range) {
    if (!range?.from || !range?.to) return null;
    const hit = PRESETS.find(p => {
        const r = computePreset(p.key);
        return r && r.from === range.from && r.to === range.to;
    });
    return hit ? hit.key : null;
}

export default function TrafficWidget({ data, loading, error, config, onConfigChange }) {
    const { t } = useTranslation();
    const { properties } = useProperties();
    const [showCustom, setShowCustom] = useState(false);

    const range = config?.dateRange || computePreset(DEFAULT_RANGE_KEY);
    const selectedProperty = config?.propertyId || '';
    const activePreset = useMemo(() => detectPreset(range), [range]);

    const [draft, setDraft] = useState(range);

    const d = data?.data;
    const cov = d?.coverage;

    const writeConfig = (patch) => {
        if (onConfigChange) onConfigChange({ ...(config || {}), dateRange: range, ...patch });
    };

    // Deliberately mount-only: this seeds a default, it must not re-fire when
    // the user later edits the range.
    useEffect(() => {
        if (!config?.dateRange && onConfigChange) {
            onConfigChange({ ...(config || {}), dateRange: computePreset(DEFAULT_RANGE_KEY) });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onRangeSelect = (value) => {
        if (value === 'custom') {
            setDraft(range);
            setShowCustom(true);
            return;
        }
        setShowCustom(false);
        const next = computePreset(value);
        if (next) writeConfig({ dateRange: next });
    };

    const applyCustom = () => {
        if (draft?.from && draft?.to && draft.from <= draft.to) {
            writeConfig({ dateRange: { from: draft.from, to: draft.to } });
            setShowCustom(false);
        }
    };

    // Daily trend, or the hour-of-day profile when the window is a single day —
    // a one-day range plotted as a daily series is a single dot.
    const singleDay = (d?.daily?.length ?? 0) <= 1;
    const series = useMemo(() => {
        if (!d) return { categories: [], values: [], name: '' };
        if (singleDay) {
            return {
                categories: (d.hourly || []).map(h => `${String(h.hour).padStart(2, '0')}`),
                values: (d.hourly || []).map(h => h.enters),
                name: t('Visitors per hour'),
            };
        }
        return {
            categories: (d.daily || []).map(x => x.day.slice(5)), // MM-DD, the year is in the picker
            values: (d.daily || []).map(x => x.enters),
            name: t('Visitors'),
        };
    }, [d, singleDay, t]);

    const chartOptions = useMemo(() => ({
        chart: { type: 'area', toolbar: { show: false }, fontFamily: 'Inter, system-ui', animations: { enabled: false } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 0.4, opacityFrom: 0.35, opacityTo: 0.05 } },
        colors: ['#3B82F6'],
        grid: { borderColor: 'var(--v3-border, #e5e7eb)', strokeDashArray: 3, padding: { left: 4, right: 4, top: 0, bottom: 0 } },
        xaxis: {
            categories: series.categories,
            tickAmount: Math.min(series.categories.length, 7),
            labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: '9px' } },
            axisTicks: { show: false },
            tooltip: { enabled: false },
        },
        yaxis: { labels: { formatter: (v) => formatNumber(Math.round(v)), style: { fontSize: '9px' } } },
        tooltip: { y: { formatter: (v) => formatNumber(v) } },
    }), [series.categories]);

    const propertyLabel = properties.find(p => p.id === selectedProperty)?.name;

    const controlStyle = {
        fontSize: 12, padding: '3px 6px', maxWidth: 150,
        border: '1px solid var(--v3-border, #e5e7eb)', borderRadius: 6,
        background: 'transparent', color: 'inherit',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '0 14px 10px', gap: 6, position: 'relative' }}>
            {/* Controls — always mounted, including while the fetch is in flight.
                A picker that disappears whenever the data reloads is unusable. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <select
                    value={selectedProperty}
                    onChange={(e) => writeConfig({ propertyId: e.target.value || undefined })}
                    aria-label={t('Select Property')}
                    style={{ ...controlStyle, flex: '1 1 auto', minWidth: 0 }}
                >
                    <option value="">{t('All properties')}</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select
                    value={activePreset || 'custom'}
                    onChange={(e) => onRangeSelect(e.target.value)}
                    aria-label={t('Date range')}
                    style={{ ...controlStyle, flex: '0 0 auto' }}
                >
                    {PRESETS.map(p => <option key={p.key} value={p.key}>{t(p.label)}</option>)}
                    <option value="custom">{t('Custom…')}</option>
                </select>
            </div>

            {/* Custom range — a popover, so it costs no height until asked for */}
            {showCustom && (
                <div style={{
                    position: 'absolute', top: 34, right: 14, zIndex: 20,
                    background: 'var(--v3-surface, #fff)', border: '1px solid var(--v3-border, #e5e7eb)',
                    borderRadius: 8, padding: 10, boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <input
                            type="date" style={controlStyle} aria-label={t('Start date')}
                            value={draft?.from || ''} max={draft?.to || undefined}
                            onChange={(e) => setDraft(s => ({ ...s, from: e.target.value }))}
                        />
                        <input
                            type="date" style={controlStyle} aria-label={t('End date')}
                            value={draft?.to || ''} min={draft?.from || undefined}
                            onChange={(e) => setDraft(s => ({ ...s, to: e.target.value }))}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button type="button" className="v3-btn v3-btn-ghost" style={{ fontSize: 12, padding: '3px 10px' }}
                            onClick={() => setShowCustom(false)}>{t('Cancel')}</button>
                        <button type="button" className="v3-btn v3-btn-primary" style={{ fontSize: 12, padding: '3px 10px' }}
                            onClick={applyCustom}>{t('Apply')}</button>
                    </div>
                </div>
            )}

            {/* Hero total on the left, supporting stats right-aligned against it.
                The headline is the number people came for; the other two are
                context and read as such at a glance. */}
            <div style={{
                display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                gap: 12, flexShrink: 0, flexWrap: 'wrap',
            }}>
                <div style={{ minWidth: 0 }}>
                    <div className="v3-kpi" style={{ fontSize: 34, lineHeight: 1.05, letterSpacing: '-0.5px' }}>
                        {d?.totalFootfall == null ? '—' : formatNumber(d.totalFootfall)}
                    </div>
                    <span className="v3-label" style={{
                        textTransform: 'none', display: 'block', marginTop: 2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260,
                    }}>
                        {propertyLabel ? t('visitors · {{property}}', { property: propertyLabel }) : t('visitors')}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingBottom: 2 }}>
                    {d?.dailyAverage != null && !singleDay && (
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>
                                {formatNumber(d.dailyAverage)}
                            </div>
                            <span className="v3-label" style={{ textTransform: 'none', fontSize: 10 }}>
                                {t('daily average')}
                            </span>
                        </div>
                    )}
                    {d?.dailyAverage != null && !singleDay && d?.peakHour != null && (
                        <span aria-hidden="true" style={{
                            width: 1, alignSelf: 'stretch', background: 'var(--v3-border, #e5e7eb)',
                        }} />
                    )}
                    {d?.peakHour != null && (
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>
                                {String(d.peakHour).padStart(2, '0')}:00
                            </div>
                            <span className="v3-label" style={{ textTransform: 'none', fontSize: 10 }}>
                                {t('busiest hour')}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Body. `position: relative` + an absolutely-filled chart layer:
                ApexCharts resolves height="100%" against a definite box, and a
                flex child with min-height:0 isn't one — it renders at zero. */}
            <div style={{ flex: 1, minHeight: 60, position: 'relative' }}>
                {loading && (
                    <div className="v3-skeleton-wrap" aria-busy="true" aria-label={t('Loading')}>
                        <div className="v3-skeleton v3-skeleton-line" style={{ width: '90%' }} />
                        <div className="v3-skeleton v3-skeleton-line" style={{ width: '75%' }} />
                    </div>
                )}
                {!loading && error && (
                    <div className="v3-body" style={{ color: 'var(--v3-red)', paddingTop: 6 }}>{t('Error')}: {error}</div>
                )}
                {!loading && !error && cov && !cov.hasFootfall && (
                    <div className="v3-body" style={{ paddingTop: 10, color: 'var(--v3-text-dim)' }}>
                        {t('No footfall data for this period')}
                    </div>
                )}
                {!loading && !error && cov?.hasFootfall && (
                    <div style={{ position: 'absolute', inset: 0 }}>
                        <Chart
                            type="area"
                            height="100%"
                            options={chartOptions}
                            series={[{ name: series.name, data: series.values }]}
                        />
                    </div>
                )}
            </div>

            {/* Visitor mix + coverage, one line each, only when there's something
                to say. A 30-day window holding 8 days of data is not a 30-day
                trend and the chart alone won't admit it. */}
            {!loading && !error && cov?.hasFootfall && (
                <div className="v3-label" style={{
                    textTransform: 'none', opacity: 0.75, flexShrink: 0,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    borderTop: '1px solid var(--v3-border, #e5e7eb)', paddingTop: 6, marginTop: 2,
                }}>
                    {(d?.demographics || []).slice(0, 3).map(dem => `${dem.category} ${dem.percentage}%`).join(' · ')}
                    {(d?.demographics?.length ? ' · ' : '')}
                    {t('{{days}} days with data', { days: formatNumber(cov.days) })}
                </div>
            )}
        </div>
    );
}
